/**
 * /payroll/iris-submissions — IRS IRIS transmission log.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/iris-submissions")({
  component: IrisSubmissionsPage,
});

interface IrisSubmission {
  id: string;
  form_type: string;
  tax_year: number;
  submission_id: string;
  status: string;
  environment: string;
  record_count: number;
  payload_summary: any;
  raw_ack: any;
  error: string | null;
  submitted_at: string;
}

const STATUS_COLOR: Record<string, "default" | "primary" | "warning" | "success" | "error" | "info"> = {
  transmitted: "info",
  accepted: "success",
  dry_run: "warning",
  rejected: "error",
  corrected: "primary",
};

const CURRENT_YEAR = new Date().getFullYear();

function IrisSubmissionsPage() {
  const [year, setYear] = useState<number | "">(CURRENT_YEAR);
  const [formType, setFormType] = useState("");
  const [view, setView] = useState<IrisSubmission | null>(null);

  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ["payroll", "iris-submissions", year, formType],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (year) qs.set("year", String(year));
      if (formType) qs.set("form_type", formType);
      return (await api(`/api/payroll/year-end/iris-submissions?${qs}`)) as IrisSubmission[];
    },
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>IRS IRIS Submissions</Typography>
          <Typography variant="body2" color="text.secondary">
            Audit log of every W-2, 1099-NEC, and 1099-MISC batch sent to the IRS.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/setup-iris.md" label="IRIS setup help" />
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <TextField select label="Tax Year" size="small" value={year}
                     onChange={(e) => setYear(e.target.value ? Number(e.target.value) : "")}
                     sx={{ minWidth: 110 }}>
            <MenuItem value="">All</MenuItem>
            {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Form" size="small" value={formType}
                     onChange={(e) => setFormType(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="W-2">W-2</MenuItem>
            <MenuItem value="1099-NEC">1099-NEC</MenuItem>
            <MenuItem value="1099-MISC">1099-MISC</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Submissions are recorded for both <code>test</code> and <code>production</code>{" "}
        environments. Test-environment submissions never reach the IRS — they appear here for
        validation history only.
      </Alert>

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted At</TableCell>
                <TableCell>Form</TableCell>
                <TableCell>Tax Year</TableCell>
                <TableCell>Records</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Submission ID</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{new Date(s.submitted_at).toLocaleString()}</TableCell>
                  <TableCell><Chip size="small" label={s.form_type} /></TableCell>
                  <TableCell>{s.tax_year}</TableCell>
                  <TableCell>{s.record_count}</TableCell>
                  <TableCell>
                    <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || "default"} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.environment} color={s.environment === "production" ? "success" : "warning"} />
                  </TableCell>
                  <TableCell><code>{s.submission_id || "—"}</code></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setView(s)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {submissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No submissions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={Boolean(view)} onClose={() => setView(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          IRIS Submission · {view?.form_type} · {view?.tax_year}
        </DialogTitle>
        <DialogContent>
          {view && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> {view.status} ({view.environment})<br />
                <strong>Submission ID:</strong> <code>{view.submission_id}</code><br />
                <strong>Submitted:</strong> {new Date(view.submitted_at).toLocaleString()}<br />
                <strong>Records:</strong> {view.record_count}
              </Typography>
              {view.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {view.error}
                </Alert>
              )}
              <Typography variant="subtitle2" mt={2}>Payload Summary</Typography>
              <Box component="pre" sx={{ bgcolor: "background.default", p: 2, borderRadius: 1,
                                          fontSize: "0.8rem", overflow: "auto", maxHeight: 200 }}>
                {JSON.stringify(view.payload_summary, null, 2)}
              </Box>
              {view.raw_ack && (
                <>
                  <Typography variant="subtitle2" mt={2}>Raw IRS Ack</Typography>
                  <Box component="pre" sx={{ bgcolor: "background.default", p: 2, borderRadius: 1,
                                              fontSize: "0.8rem", overflow: "auto", maxHeight: 240 }}>
                    {JSON.stringify(view.raw_ack, null, 2)}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
